import PowersportsAsset from '../../asset/powersports-asset';
import PowersportsCta from '../../cta/powersports-cta';
import Cta, { CtaStyle } from '../../cta';
import Platform, { PageTypes, PlatformAssetWrapperMap } from '..';
import { createStyleTag, StyleTag } from '../../util/create-style-tag';
// CSS Style files content - Each imported variable is holding the content of the file as a string
import srpStyleContent from './style/srp.css';
import vdpStyleContent from './style/vdp.css';
import homepageStyleContent from './style/homepage.css';
import AssetWrapper from '../../asset/wrappers';
import { StringValue, WrappedHTMLElement } from '../../util/parse-data';
import { PageNotReady } from '../../errors';
import configuration from '../../configuration';
import { logToPartnerVisit } from '../../util/logging';
import { OlError } from '../../events';

/**
 * Finds or creates a target element (either .ol-target-element or .ol-sidebar-target-element) inside the given container. If not found, appends to document.body.
 * Throws PageNotReady if document.body is not available.
 */
export function getOrCreateTargetElement(
  container: HTMLElement,
  targetClass: 'ol-target-element' | 'ol-sidebar-target-element' = 'ol-target-element'
): HTMLElement {
  let targetLocation = container.querySelector<HTMLElement>(`.${targetClass}`);
  if (targetLocation) return targetLocation;

  const renderLocation = document.body;
  if (!renderLocation) throw new PageNotReady('Render location not found in the container.');

  targetLocation = document.createElement('div');
  targetLocation.classList.add(targetClass);
  if (targetClass === 'ol-target-element' && container === document.body) {
    targetLocation.classList.add('ol-sidebar-target-element');
  }
  renderLocation.append(targetLocation);
  return targetLocation;
}

function getOrCreateBodyTargetElement(targetClass: string): HTMLElement {
  const renderLocation = document.body;
  if (!renderLocation) throw new PageNotReady('Render location not found in the container.');
  let targetLocation = renderLocation.querySelector<HTMLElement>(`.${targetClass}`);
  if (!targetLocation) {
    targetLocation = document.createElement('div');
    targetLocation.classList.add(targetClass);
    renderLocation.append(targetLocation);
  }
  return targetLocation;
}

/**
 * Dealerspike V7 Vehicle Detail Page (VDP) Wrapper
 */
export class DealerspikeV7VdpAssetWrapper extends AssetWrapper<PowersportsAsset> {
  private renderedCtas: Record<string, Cta> = {};

  getDocumentStyles(): StyleTag[] {
    return [createStyleTag(vdpStyleContent.toString(), 'vdp-style')];
  }

  render(_e?: Event, _forceCtaRender?: boolean): void {
    this.renderAllCtas();
  }

  renderAllCtas() {
    (['standard', 'sidebar'] as CtaStyle[]).forEach((style) => {
      if (style === 'sidebar') {
        if (!configuration.enable_sidebar_cta_widget || window.innerWidth < 768) {
          return;
        }
      }
      this.renderCta(style);
    });
  }

  renderCta(style: CtaStyle) {
    try {
      if (!this.asset || this.asset.filterCTA(configuration.filter_groups)) return;
      if (!this.renderedCtas[style]) {
        const cta = new PowersportsCta(this, { style });
        this.renderedCtas[style] = cta;
        cta.render(undefined, true, style);
      } else {
        this.renderedCtas[style].render(undefined, true, style);
      }
      if (this.renderedCtas[style].isRendered()) {
        logToPartnerVisit(this.platform.pageType).catch((error) => console.log(error));
      }
    } catch (error) {
      if (error instanceof PageNotReady) {
        this.platform.onNextCycle(() => this.renderCta(style));
      } else {
        this.platform.eventBus.dispatchEvent(
          new OlError({ detail: { error, msg: `Failed to render DS7 VDP CTA (${style})` } })
        );
      }
    }
  }

  getRenderLocation(style: CtaStyle): HTMLElement {
    return this.getRenderLocationByType(style);
  }

  getRenderLocationByType(ctaType: CtaStyle): HTMLElement {
    const selectorMap: Record<string, () => HTMLElement> = {
      standard: () => {
        return this.prependRenderLocationBySelector('ul.ulCallToAction', 'li');
      },
      sidebar: () => {
        return getOrCreateTargetElement(this.container, 'ol-sidebar-target-element');
      }
    };
    const getTarget = selectorMap[ctaType];
    if (!getTarget) throw new Error(`Unknown CTA type: ${ctaType}`);
    return getTarget();
  }

  getVehicleDataFromSpecTable(vehicleDataTypeClass: string) {
    const vehicleDataItem = this.container.querySelector<HTMLElement>(`
      div.invUnitInfoTable ul > li.liUnit.${vehicleDataTypeClass} > .unitValue,
      #accordionSpecifications ul > li.liUnit.${vehicleDataTypeClass} > .unitValue
    `);
    if (!vehicleDataItem) return null;
    return new WrappedHTMLElement(vehicleDataItem);
  }

  parseAssetData(): PowersportsAsset {
    const wrappedContainer = new WrappedHTMLElement(this.container);
    return new PowersportsAsset({
      price:
        new WrappedHTMLElement(this.container, '.unitPrice .price.price-price .price-value')
          .getTextContent()
          ?.toFloat() ??
        new WrappedHTMLElement(this.container, '.unitPrice .price.price-pricetext .price-value')
          .getTextContent()
          ?.toFloat(),
      originalPrice: new WrappedHTMLElement(this.container, '.unitPrice .price.price-retail .price-value')
        .getTextContent()
        ?.toFloat(),
      make: wrappedContainer.getAttr('data-unit-make')?.value,
      model: wrappedContainer.getAttr('data-unit-model')?.value,
      year: wrappedContainer.getAttr('data-unit-year')?.value,
      category: (
        wrappedContainer.getAttr('data-unit-category') ||
        this.getVehicleDataFromSpecTable('LiInvcategory')?.getTextContent()
      )?.value,
      subcategory: (
        wrappedContainer.getAttr('data-unit-subcategory') ||
        this.getVehicleDataFromSpecTable('LiInvsubcategory')?.getTextContent()
      )?.value,
      id: wrappedContainer.getAttr('data-unit-id')?.value,
      condition: (
        wrappedContainer.getAttr('data-unit-condition') ||
        this.getVehicleDataFromSpecTable('LiInvcondition')?.getTextContent()
      )?.value,
      color: this.getVehicleDataFromSpecTable('LiInvcolor')?.getTextContent()?.value,
      vin: this.getVehicleDataFromSpecTable('LiInvvin')?.getTextContent()?.value,
      stockNumber: this.getVehicleDataFromSpecTable('LiInvstock-number')?.getTextContent()?.value,
      on_sale: this.container.querySelector('.price-pricedifference') != null,
      location: this.getVehicleDataFromSpecTable('LiInvlocation')?.getTextContent()?.value
    });
  }

  createCta(): Cta {
    return new PowersportsCta(this, { style: 'standard' });
  }
}

/**
 * Dealerspike V7 Search Results Page (SRP) Wrapper
 */
export class DealerspikeV7SrpAssetWrapper extends AssetWrapper<PowersportsAsset> {
  private static bannerRendered = false;
  private static sidebarRendered = false;

  getDocumentStyles(): StyleTag[] {
    return [createStyleTag(srpStyleContent.toString(), 'srp-style')];
  }

  private renderedCtas: Record<string, Cta> = {};

  render(_e?: Event, _forceCtaRender?: boolean): void {
    this.renderAllCtas();
  }

  // List of CTAs to render for this page
  renderAllCtas() {
    (['standard', 'banner', 'sidebar'] as CtaStyle[]).forEach((style) => this.renderCta(style));
  }

  renderCta(style: CtaStyle) {
    try {
      if (style === 'banner' && (DealerspikeV7SrpAssetWrapper.bannerRendered || !configuration.enable_srp_cta_widget)) {
        return;
      }
      if (
        style === 'sidebar' &&
        (DealerspikeV7SrpAssetWrapper.sidebarRendered ||
          !configuration.enable_sidebar_cta_widget ||
          window.innerWidth < 768)
      ) {
        return;
      }
      if (!this.asset || this.asset.filterCTA(configuration.filter_groups)) return;

      // Only create the CTA once for this type
      if (!this.renderedCtas[style]) {
        const cta = new PowersportsCta(this, { style });
        this.renderedCtas[style] = cta;
        cta.render(undefined, true, style);
      } else {
        this.renderedCtas[style].render(undefined, true, style);
      }
      if (style === 'banner') {
        DealerspikeV7SrpAssetWrapper.bannerRendered = true;
      }
      if (style === 'sidebar') {
        DealerspikeV7SrpAssetWrapper.sidebarRendered = true;
      }

      // Report rendering to analytics
      if (this.renderedCtas[style].isRendered()) {
        logToPartnerVisit(this.platform.pageType).catch((error) => console.log(error));
      }
    } catch (error) {
      // If the DOM is not ready yet, retry in the next render cycle
      if (error instanceof PageNotReady) {
        this.platform.onNextCycle(() => this.renderCta(style));
      } else {
        this.platform.eventBus.dispatchEvent(
          new OlError({ detail: { error, msg: `Failed to render DS7 CTA (${style})` } })
        );
      }
    }
  }

  getRenderLocation(style: CtaStyle): HTMLElement {
    return this.getRenderLocationByType(style);
  }

  // Dynamically determine where each CTA should be inserted in the DOM
  getRenderLocationByType(ctaStyle: CtaStyle): HTMLElement {
    const selectorMap: Record<string, () => HTMLElement> = {
      // Standard CTA placed alongside each asset in the SRP
      standard: () => {
        return this.prependRenderLocationBySelector('.v7list-vehicle__info-column--buttons');
      },

      // Banner CTA placed on top of the SRP
      banner: () => {
        const renderLocation = document.querySelector('.v7list-subheader');
        if (!renderLocation) throw new PageNotReady('Render location not found in the container.');

        const targetLocation = document.createElement('div');
        targetLocation.classList.add('ol-banner-target-element');
        renderLocation.prepend(targetLocation);

        return targetLocation;
      },
      sidebar: () => {
        const renderLocation = document.body;
        if (!renderLocation) throw new PageNotReady('Render location not found in the container.');

        const targetLocation = document.createElement('div');
        targetLocation.classList.add('ol-sidebar-target-element');
        renderLocation.prepend(targetLocation);

        return targetLocation;
      }
    };

    const getTarget = selectorMap[ctaStyle];
    if (!getTarget) throw new Error(`Unknown CTA type: ${ctaStyle}`);
    return getTarget();
  }

  getValueFromSpecs(key: string): StringValue | null {
    // Get Spec Element from DOM
    const specElement = this.container.querySelector<HTMLElement>(`.vehicle-specs__item--${key}`);
    // Get Element containing the value we care about, and create a Wrapped HTML Element
    const specValue = new WrappedHTMLElement(specElement, '.vehicle-specs__value');
    // Special case where DSv7 can use '---' as a null value
    if (specValue.getTextContent()?.value === '___') return null;
    // return what we've found
    return specValue.getTextContent();
  }

  parseAssetData(): PowersportsAsset {
    const wrappedContainer = new WrappedHTMLElement(this.container);
    return new PowersportsAsset({
      price:
        new WrappedHTMLElement(
          this.container.querySelector<HTMLElement>('.vehicle-price--current'),
          '.vehicle-price__price'
        )
          .getTextContent()
          ?.toFloat() ??
        new WrappedHTMLElement(
          this.container.querySelector<HTMLElement>('.vehicle-price--current'),
          '.vehicle-price__label'
        )
          .getTextContent()
          ?.toFloat(),
      originalPrice: new WrappedHTMLElement(
        this.container.querySelector<HTMLElement>('.vehicle-price--old'),
        '.vehicle-price__price'
      )
        .getTextContent()
        ?.toFloat(),
      make: wrappedContainer.getAttr('data-unit-make')?.value,
      model: wrappedContainer.getAttr('data-unit-model')?.value,
      category: wrappedContainer.getAttr('data-unit-category')?.value || this.getValueFromSpecs('category')?.value,
      subcategory:
        wrappedContainer.getAttr('data-unit-subcategory')?.value || this.getValueFromSpecs('subcategory')?.value,
      year: wrappedContainer.getAttr('data-unit-year')?.value,
      id: wrappedContainer.getAttr('data-unit-id')?.value,
      color: this.getValueFromSpecs('color')?.value,
      vin: this.getValueFromSpecs('vin')?.value,
      stockNumber: this.getValueFromSpecs('stock-number')?.value,
      condition: this.getValueFromSpecs('condition')?.value,
      on_sale: !!this.container.querySelector('.vehicle-price--savings'),
      location: this.getValueFromSpecs('location')?.value
    });
  }

  static getPageContainer(): HTMLElement {
    const selector = '#v7results';
    const container = document.querySelector<HTMLElement>(selector);
    if (!container) throw new Error(`Page container not found: ${selector}`);
    return container;
  }

  static shouldWatchElements(): boolean {
    return true;
  }
}

export class DealerspikeV7HomepageWrapper extends AssetWrapper<PowersportsAsset> {
  private renderedCtas: Record<string, Cta> = {};

  getDocumentStyles(): StyleTag[] {
    return [createStyleTag(homepageStyleContent.toString(), 'homepage-style')];
  }

  render(_e?: Event, _forceCtaRender?: boolean): void {
    this.renderAllCtas();
  }

  renderAllCtas() {
    (['banner', 'sidebar'] as CtaStyle[]).forEach((style) => this.renderCta(style));
  }

  renderCta(style: CtaStyle) {
    try {
      if (style === 'banner' && !configuration.enable_homepage_cta_widget) {
        return;
      }
      if (style === 'sidebar' && (!configuration.enable_sidebar_cta_widget || window.innerWidth < 768)) {
        return;
      }
      if (!this.asset || this.asset.filterCTA(configuration.filter_groups)) return;
      if (!this.renderedCtas[style]) {
        const cta = new PowersportsCta(this, { style });
        this.renderedCtas[style] = cta;
        cta.render(undefined, true, style);
      } else {
        this.renderedCtas[style].render(undefined, true, style);
      }
      if (this.renderedCtas[style].isRendered()) {
        logToPartnerVisit(this.platform.pageType).catch((error) => console.log(error));
      }
    } catch (error) {
      if (error instanceof PageNotReady) {
        this.platform.onNextCycle(() => this.renderCta(style));
      } else {
        this.platform.eventBus.dispatchEvent(
          new OlError({ detail: { error, msg: `Failed to render DS7 Homepage CTA (${style})` } })
        );
      }
    }
  }

  getRenderLocation(style: CtaStyle = 'sidebar'): HTMLElement {
    return this.getRenderLocationByType(style);
  }

  getRenderLocationByType(ctaType: CtaStyle): HTMLElement {
    const selectorMap: Record<string, () => HTMLElement> = {
      banner: () => getOrCreateBodyTargetElement('ol-target-element'),
      sidebar: () => getOrCreateBodyTargetElement('ol-sidebar-target-element')
    };
    const getTarget = selectorMap[ctaType];
    if (!getTarget) throw new Error(`Unknown CTA type: ${ctaType}`);
    return getTarget();
  }

  parseAssetData(): PowersportsAsset {
    // Homepage does not parse assets, so we return an empty asset
    return new PowersportsAsset({});
  }

  static getPageContainer(): HTMLElement {
    const pageContainer = document.querySelector<HTMLElement>('.content-home');
    if (pageContainer) return pageContainer;
    else throw new Error('Page container not found.');
  }

  createCta(): Cta {
    return new PowersportsCta(this, { style: 'banner' });
  }

  static shouldWatchElements(): boolean {
    return true;
  }

  shouldRender(): boolean {
    if (!configuration.enable_homepage_cta_widget) return false;
    return super.shouldRender();
  }
}

export class DealerspikeV7GeneralWrapper extends AssetWrapper<PowersportsAsset> {
  getRenderLocation(): HTMLElement {
    return getOrCreateTargetElement(this.container);
  }

  parseAssetData(): PowersportsAsset {
    // Sidebar widget does not parse assets, so we return an empty asset
    return new PowersportsAsset({});
  }

  static getPageContainer(): HTMLElement {
    return document.body;
  }

  createCta(): Cta {
    return new PowersportsCta(this, { style: 'sidebar' });
  }

  static shouldWatchElements(): boolean {
    return true;
  }

  shouldRender(): boolean {
    return window.innerWidth >= 768 && configuration.enable_sidebar_cta_widget;
  }
}

/**
 * Dealerspike v7 platform class
 */
export default class DealerspikeV7 extends Platform {
  pageTypeMap: PlatformAssetWrapperMap = {
    [PageTypes.SRP]: DealerspikeV7SrpAssetWrapper,
    [PageTypes.VDP]: DealerspikeV7VdpAssetWrapper,
    [PageTypes.HOMEPAGE]: DealerspikeV7HomepageWrapper,
    [PageTypes.GENERAL]: DealerspikeV7GeneralWrapper
  };

  watchElements() {
    window.addEventListener('resize', () => {
      this.assetWrappers?.forEach((wrapper) => {
        wrapper.render();
      });
    });
  }

  determinePageType(): PageTypes {
    if (document.querySelector('div#template') != null) {
      return PageTypes.VDP;
    }
    if (document.querySelector('#v7results')) {
      return PageTypes.SRP;
    }
    if (document.querySelector('.content-home')) {
      return PageTypes.HOMEPAGE;
    }
    return PageTypes.GENERAL;
  }

  getAssetContainers(): HTMLElement[] {
    let containers;
    if (this.pageType === PageTypes.SRP) {
      containers = Array.from(document.querySelectorAll<HTMLElement>('.v7list-results__item'));
    } else if (this.pageType === PageTypes.VDP) {
      containers = Array.from(document.querySelectorAll<HTMLElement>('#template'));
    } else if (this.pageType === PageTypes.HOMEPAGE || this.pageType === PageTypes.GENERAL) {
      return [document.body];
    } else {
      throw new Error('pageType is not supported by this platform');
    }

    if (!containers?.length) {
      throw new PageNotReady('Containers not yet found');
    }
    return containers;
  }
}
